<template>
  <div class="gallery-container">
    <!-- 添加TopBar组件 -->
    <TopBar :user="user" :apps="apps" />

    <!-- 页面头部 -->
    <div class="gallery-header">
      <h1 class="gallery-title">
        <i class="icon">📚</i>
        {{ galleryType === 'galleryE' ? '英语单词库' : '中文字词库' }}
      </h1>
      <!-- 添加测试按钮 -->
      <div v-if="activeTab === 'standard'">
        <button @click="loadStandardLibrary">加载标准库数据</button>
        <button @click="reloadStandardLibrary">重新加载数据</button>
        <button @click="testApiService">测试API服务</button>
        <button @click="showStandardDataStructure">显示数据结构</button>
        <button @click="validateStandardData">验证数据完整性</button>
        <button @click="formatStandardData">格式化显示数据</button>
      </div>
    </div>

    <!-- 主要内容区域 -->
    <div class="gallery-main" v-if="!loading">
      <!-- 选项卡导航 -->
      <div class="tabs">
        <button :class="['tab', { active: activeTab === 'standard' }]" @click="switchTab('standard')">
          标准库
        </button>
        <button :class="['tab', { active: activeTab === 'share' }]" @click="switchTab('share')">
          共享库
        </button>
        <button :class="['tab', { active: activeTab === 'my' }]" @click="switchTab('my')">
          我的
        </button>
        <!-- 添加默写模式选项卡 -->
        <button :class="['tab', { active: activeTab === 'practice' }]" @click="switchTab('practice')"
          v-if="selectedWordList">
          默写模式
        </button>
      </div>

      <!-- 标准库内容 -->
      <div v-if="activeTab === 'standard'" class="tab-content">
        <!-- 面包屑导航 -->
        <div class="breadcrumb" v-if="breadcrumbPaths.length > 0">
          <span @click="resetToRoot" class="breadcrumb-link">标准库</span>
          <span v-for="(path, index) in breadcrumbPaths" :key="index">
            >
            <span v-if="index < breadcrumbPaths.length - 1" @click="navigateToPath(index)" class="breadcrumb-link">
              {{ path }}
            </span>
            <span v-else class="breadcrumb-current">{{ path }}</span>
          </span>
          <button @click="goBack" class="back-btn" v-if="breadcrumbPaths.length > 0">返回</button>
        </div>

        <!-- 分类导航 -->
        <div class="category-level">
          <h3>{{ currentLevelTitle }}</h3>

          <!-- 显示文件夹 -->
          <div class="category-grid" v-if="currentFolders.length > 0">
            <div v-for="(folder, index) in currentFolders" :key="index" class="category-card"
              @click="enterFolder(folder)">
              <div class="category-icon">📁</div>
              <h4 class="category-name">{{ folder }}</h4>
            </div>
          </div>

          <!-- 显示文件 -->
          <div class="books-grid" v-if="currentFiles.length > 0">
            <div v-for="(file, index) in currentFiles" :key="index" class="book-card">
              <div class="book-info">
                <h4 class="book-name">{{ getFileDisplayName(file) }}</h4>
                <p class="book-desc">{{ file }}</p>
              </div>
              <div class="book-actions">
                <button v-if="!isBookImported(getFileDisplayName(file))" @click="importBook(file)" class="import-btn">
                  导入
                </button>
                <span v-else class="imported-tag">已导入</span>
              </div>
            </div>
          </div>

          <!-- 空状态 -->
          <div v-if="currentFolders.length === 0 && currentFiles.length === 0" class="empty-state">
            <p>该分类下暂无内容</p>
          </div>
        </div>
      </div>

      <!-- 共享库内容 -->
      <div v-if="activeTab === 'share'" class="tab-content">
        <div class="empty-state">
          <p>共享库功能正在开发中...</p>
        </div>
      </div>

      <!-- 我的内容 -->
      <div v-if="activeTab === 'my'" class="tab-content">
        <div class="books-grid">
          <div v-for="(book, index) in myBooks" :key="index" class="book-card my-book">
            <div class="book-info">
              <h4 class="book-name">{{ book.name }}</h4>
              <p class="book-desc">{{ book.desc }}</p>
              <div class="book-stats">
                <span class="stat-item">进度: {{ book.percent }}%</span>
                <span class="stat-item">掌握: {{ book.finished }}/{{ book.count }}</span>
              </div>
            </div>
            <div class="book-actions">
              <button @click="editBook(book)" class="edit-btn">编辑单词表</button>
              <button @click="startMemorization(book)" class="practice-btn">开始默写</button>
              <button @click="deleteBook(book)" class="delete-btn">删除</button>
            </div>
          </div>

          <!-- 添加新单词表卡片 -->
          <div class="book-card add-card" @click="showAddDialog">
            <div class="add-content">
              <span class="add-icon">+</span>
              <p>新建单词表</p>
            </div>
          </div>
        </div>
      </div>

      <!-- 默写模式内容 -->
      <div v-if="activeTab === 'practice' && selectedWordList" class="tab-content">
        <div class="abhs-content">
          <div class="content-header">
            <div class="navigation">
              <button @click="switchToPreviousWordList" class="nav-btn" :disabled="currentWordListIndex <= 0">
                ← 上一章节
              </button>
              <h2 class="current-book">{{ selectedWordList.name }}</h2>
              <button @click="switchToNextWordList" class="nav-btn"
                :disabled="currentWordListIndex >= myBooks.length - 1">
                下一章节 →
              </button>
            </div>
            <div class="controls">
              <button @click="startPractice" class="control-btn start-btn" v-if="!isPracticing">
                开始默写
              </button>
              <button @click="stopPractice" class="control-btn stop-btn" v-else>
                停止默写
              </button>
              <button @click="showRanking" class="control-btn ranking-btn">
                排行榜
              </button>
            </div>
          </div>

          <!-- 默写界面 -->
          <div class="practice-area" v-if="isPracticing">
            <div class="word-display" v-if="currentWord">
              <div class="word">{{ currentWord.name }}</div>
              <div class="description">{{ currentWord.desc }}</div>
            </div>
            <div class="user-input">
              <input v-model="userAnswer" @keyup.enter="checkAnswer" placeholder="请输入答案..." class="answer-input"
                ref="answerInput" />
              <button @click="checkAnswer" class="check-btn">检查</button>
            </div>
            <div class="practice-stats">
              <div class="stat-item">
                <span class="label">进度:</span>
                <span class="value">{{ currentWordIndex + 1 }}/{{ shuffledWords.length }}</span>
              </div>
              <div class="stat-item">
                <span class="label">正确:</span>
                <span class="value correct">{{ correctCount }}</span>
              </div>
              <div class="stat-item">
                <span class="label">错误:</span>
                <span class="value wrong">{{ wrongCount }}</span>
              </div>
              <div class="stat-item">
                <span class="label">正确率:</span>
                <span class="value">{{ accuracy }}%</span>
              </div>
            </div>
          </div>

          <!-- 结果统计 -->
          <div class="results" v-if="showResults">
            <h3>默写结果</h3>
            <div class="result-stats">
              <div class="stat-item">
                <span class="label">总题数:</span>
                <span class="value">{{ shuffledWords.length }}</span>
              </div>
              <div class="stat-item">
                <span class="label">正确:</span>
                <span class="value correct">{{ correctCount }}</span>
              </div>
              <div class="stat-item">
                <span class="label">错误:</span>
                <span class="value wrong">{{ wrongCount }}</span>
              </div>
              <div class="stat-item">
                <span class="label">正确率:</span>
                <span class="value">{{ accuracy }}%</span>
              </div>
            </div>
            <div class="wrong-words" v-if="wrongWords.length > 0">
              <h4>错误单词</h4>
              <div class="wrong-words-list">
                <div v-for="(word, index) in wrongWords" :key="index" class="wrong-word-item">
                  <span class="word">{{ word.name }}</span>
                  <span class="desc">{{ word.desc }}</span>
                </div>
              </div>
            </div>
            <div class="result-actions">
              <button @click="restartPractice" class="action-btn">重新开始</button>
              <button @click="goToMyTab" class="action-btn">选择其他章节</button>
              <button @click="goToAbhsMain" class="action-btn">返回主页面</button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 添加单词表对话框 -->
    <div v-if="showAddDialogFlag" class="dialog-overlay" @click="closeAddDialog">
      <div class="dialog" @click.stop>
        <h3>新建单词表</h3>
        <div class="form-group">
          <label>单词表名称:</label>
          <input v-model="newBookName" type="text" class="form-input" placeholder="请输入单词表名称">
        </div>
        <div class="form-group">
          <label>描述:</label>
          <textarea v-model="newBookDesc" class="form-textarea" placeholder="请输入描述"></textarea>
        </div>
        <div class="dialog-actions">
          <button @click="closeAddDialog" class="cancel-btn">取消</button>
          <button @click="addNewBook" class="confirm-btn">确定</button>
        </div>
      </div>
    </div>

    <!-- 加载状态 -->
    <div v-if="loading" class="loading-overlay">
      <div class="loading-spinner">
        <div class="spinner"></div>
        <p>{{ loadingMessage }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import TopBar from '@/components/TopBar.vue'
import AuthService from '@/services/authService.js'
// import UserService from '@/services/userService.js'
import AbhsService from '@/services/abhsService.js'
import UserMainService from '@/services/userMainService.js'
import config from '@/config'

export default {
  name: 'GalleryView',
  components: {
    TopBar
  },
  setup() {
    const route = useRoute()
    const router = useRouter()

    // 响应式数据
    const user = ref({
      name: '用户名',
      id: 1,
      userType: 1,
      avatar: require('@/assets/logo.png')
    })

    const apps = ref([])
    const loading = ref(true)
    const loadingMessage = ref('加载中...')
    const galleryType = ref('galleryE') // galleryE 或 galleryC
    const activeTab = ref('my') // standard, share, my, practice
    const activeCategory = ref('')

    // 标准库层级选择
    const breadcrumbPaths = ref([]) // 面包屑导航路径
    const selectedCategoryLevel1 = ref('')
    const selectedCategoryLevel2 = ref('')

    // 数据
    const myBooks = ref([])
    const standardData = ref({})
    const categoryList = ref([])

    // 添加单词表对话框
    const showAddDialogFlag = ref(false)
    const newBookName = ref('')
    const newBookDesc = ref('')

    // 默写相关数据
    const selectedWordList = ref(null)
    const currentWordListIndex = ref(-1)
    const isPracticing = ref(false)
    const shuffledWords = ref([])
    const currentWordIndex = ref(0)
    const currentWord = computed(() => shuffledWords.value[currentWordIndex.value])
    const userAnswer = ref('')
    const correctCount = ref(0)
    const wrongCount = ref(0)
    const wrongWords = ref([])
    const showResults = ref(false)

    // 计算属性
    const accuracy = computed(() => {
      const total = correctCount.value + wrongCount.value
      return total > 0 ? Math.round((correctCount.value / total) * 100) : 0
    })

    // 计算当前层级标题
    const currentLevelTitle = computed(() => {
      if (breadcrumbPaths.value.length === 0) {
        return galleryType.value === 'galleryE' ? '英语单词库' : '中文字词库';
      } else {
        return breadcrumbPaths.value[breadcrumbPaths.value.length - 1];
      }
    });

    // 计算当前层级的数据
    const currentLevelData = computed(() => {
      if (breadcrumbPaths.value.length === 0) {
        // 根据galleryType确定根节点
        const rootKey = galleryType.value === 'galleryE' ? 'E' : 'C';
        return standardData.value[rootKey] || {};
      } else {
        // 获取当前路径的数据
        const currentPath = breadcrumbPaths.value[breadcrumbPaths.value.length - 1];
        return standardData.value[currentPath] || {};
      }
    });

    // 计算当前层级的文件夹
    const currentFolders = computed(() => {
      const data = currentLevelData.value;
      return data.folders || [];
    });

    // 计算当前层级的文件
    const currentFiles = computed(() => {
      const data = currentLevelData.value;
      return data.files || [];
    });

    // 加载用户信息
    const loadUserInfo = () => {
      const currentUser = AuthService.getCurrentUser()
      if (currentUser) {
        console.log('当前用户信息:', currentUser);
        user.value.name = currentUser.nickname || currentUser.username || '用户名';
        user.value.id = currentUser.userid || currentUser.id || 1;
        user.value.userType = currentUser.userType || 1;
        console.log("icon:", currentUser.icon);
        // 如果有头像信息，则更新头像路径
        if (currentUser.icon) {
          // 规范化路径分隔符，将反斜杠转换为正斜杠
          const normalizedIcon = currentUser.icon.replace(/\\/g, '/');
          console.log('规范化后的icon路径:', normalizedIcon);

          // 构建完整的后端服务器地址，确保头像从8000端口获取
          if (normalizedIcon.startsWith('http')) {
            console.log('完整URL:', normalizedIcon);
            // 如果已经是完整URL，则直接使用
            user.value.avatar = normalizedIcon;
          } else if (normalizedIcon.startsWith('/extra-files/uploads/')) {
            console.log('extra-files URL:', normalizedIcon);
            // 从后端服务器的8000端口获取头像
            user.value.avatar = `${config.API_BASE_URL}${normalizedIcon}`;
          } else if (normalizedIcon.startsWith('uploads/')) {
            console.log('uploads URL:', normalizedIcon);
            // 从后端服务器的8000端口获取头像
            //user.value.avatar = `${config.API_BASE_URL}/extra-files/${normalizedIcon}`;

            user.value.avatar = `${config.API_BASE_URL}/extra-files/${normalizedIcon}`;
            //查看地址：`${config.API_BASE_URL}/extra-files/${normalizedIcon}`;
            console.log('user.value.avatar', user.value.avatar);
          } else {
            console.log('img URL:', normalizedIcon);
            user.value.avatar = `${config.API_BASE_URL}/extra-files/uploads/${normalizedIcon}`;
          }
        } else {
          console.log('使用默认头像');
          // 使用默认头像
          user.value.avatar = require('@/assets/logo.png');
        }
      }
    }

    // 加载应用信息
    const loadAppInfo = async () => {
      try {
        const response = await UserMainService.getAllApps(user.value.userType)
        if (response.status === 200 && response.apps) {
          apps.value = response.apps.map(app => ({
            id: app.id,
            name: app.name,
            desc: app.desc,
            link: app.name === '快速抢答' ? '/abhs' : app.link,
            icon: app.icon || 'code',
            used: app.used || false
          }))
        }
      } catch (error) {
        console.error('获取应用数据失败:', error)
        apps.value = [
          { id: 1, name: 'OJ练习', desc: '在线编程练习', link: '/CppOJ/', icon: 'code', used: true },
          { id: 2, name: '任务清单', desc: '待办事项管理', link: '/todo', icon: 'list', used: true },
          { id: 3, name: '时间线', desc: '时间记录管理', link: '/TimeLine/', icon: 'timeline', used: false },
          { id: 4, name: '知识图谱', desc: '知识关系展示', link: '/Graph/', icon: 'share', used: false },
          { id: 5, name: '快速抢答', desc: '抢答与记忆训练', link: '/abhs', icon: 'quiz', used: false },
          { id: 6, name: '文件管理', desc: '文件上传下载', link: '/FileManage/', icon: 'folder', used: false },
          { id: 7, name: '数据可视化', desc: '数据图表展示', link: '/DataVisual/', icon: 'bar_chart', used: false }
        ]
      }
    }

    // 获取我的单词表
    const getMyWordLists = async () => {
      try {
        loading.value = true
        loadingMessage.value = '加载单词表...'
        const response = await AbhsService.getWordList(user.value.id)
        if (response.status === 200) {
          myBooks.value = response.mybooks || []
          if (myBooks.value.length > 0 && currentWordListIndex.value === -1) {
            currentWordListIndex.value = 0
          }
        }
      } catch (error) {
        console.error('获取单词表失败:', error)
      } finally {
        loading.value = false
      }
    }

    // 获取标准库数据
    const getStandardLibrary = async () => {
      try {
        loading.value = true
        loadingMessage.value = '加载标准库...'
        const response = await AbhsService.getStandardLibrary()
        console.log('标准库数据响应:', response) // 添加日志以便调试

        // 检查响应结构
        console.log('响应类型:', typeof response)
        console.log('响应数据结构:', Object.keys(response))

        if (response.status === 200) {
          // 确保正确处理响应数据结构
          // 根据API返回的实际结构，数据应该在response.data.data中
          if (response.data && response.data.data) {
            standardData.value = response.data.data
          } else if (response.data) {
            standardData.value = response.data
          } else {
            standardData.value = {}
          }

          // 提取第一层分类列表
          if (standardData.value.root && standardData.value.root.folders) {
            categoryList.value = standardData.value.root.folders
          }
          console.log('处理后的标准库数据:', standardData.value)
          console.log('分类列表:', categoryList.value)
        } else {
          console.error('获取标准库数据失败:', response.message)
        }
      } catch (error) {
        console.error('获取标准库数据失败:', error)
      } finally {
        loading.value = false
      }
    }

    // 切换选项卡
    const switchTab = (tab) => {
      activeTab.value = tab
      if (tab === 'standard') {
        // 总是尝试加载标准库数据
        getStandardLibrary()
      } else if (tab === 'my' && myBooks.value.length === 0) {
        getMyWordLists()
      }

      // 清除标准库层级选择
      if (tab !== 'standard') {
        clearCategorySelection()
        breadcrumbPaths.value = []; // 清除面包屑路径
      }
    }

    // 选择第一层分类
    const selectCategoryLevel1 = (category) => {
      selectedCategoryLevel1.value = category
      selectedCategoryLevel2.value = ''
    }

    // 选择第二层分类
    const selectCategoryLevel2 = (subcategory) => {
      selectedCategoryLevel2.value = subcategory
    }

    // 清除所有分类选择
    const clearCategorySelection = () => {
      selectedCategoryLevel1.value = ''
      selectedCategoryLevel2.value = ''
    }

    // 清除第二层分类选择
    const clearCategoryLevel2Selection = () => {
      selectedCategoryLevel2.value = ''
    }

    // 进入文件夹
    const enterFolder = (folderName) => {
      breadcrumbPaths.value.push(folderName);
    };

    // 获取文件显示名称
    const getFileDisplayName = (fileName) => {
      return fileName.replace('.xlsx', '');
    };

    // 检查书籍是否已导入
    const isBookImported = (bookName) => {
      return myBooks.value.some(book => book.name === bookName);
    };

    // 返回上一级
    const goBack = () => {
      if (breadcrumbPaths.value.length > 0) {
        breadcrumbPaths.value.pop();
      }
    };

    // 重置到根目录
    const resetToRoot = () => {
      breadcrumbPaths.value = [];
    };

    // 导航到指定路径
    const navigateToPath = (index) => {
      breadcrumbPaths.value = breadcrumbPaths.value.slice(0, index + 1);
    };

    // 获取第一层分类列表
    const getRootCategories = () => {
      console.log('获取第一层分类列表...')
      console.log('当前标准库数据:', standardData.value)

      // 检查数据是否存在
      if (!standardData.value) {
        console.log('标准库数据为空')
        return []
      }

      // 根据galleryType确定根节点
      const rootKey = galleryType.value === 'galleryE' ? 'E' : 'C';

      // 检查是否有对应根节点
      if (!standardData.value[rootKey]) {
        console.log(`缺少${rootKey}节点`)
        return []
      }

      // 检查是否有folders字段
      if (!standardData.value[rootKey].folders) {
        console.log(`${rootKey}节点缺少folders字段`)
        return []
      }

      console.log('第一层分类列表:', standardData.value[rootKey].folders)
      return standardData.value[rootKey].folders
    }

    // 获取第二层子分类
    const getSubcategoriesLevel2 = (category) => {
      // 添加调试日志
      console.log('获取子分类:', category, '当前标准库数据:', standardData.value)

      // 检查数据是否存在
      if (!standardData.value || !standardData.value[category]) {
        console.log('未找到分类数据:', category)
        return []
      }

      // 检查是否有folders字段
      if (!standardData.value[category].folders) {
        console.log('分类数据中缺少folders字段:', category)
        return []
      }

      console.log('子分类数据:', standardData.value[category].folders)
      return standardData.value[category].folders
    }

    // 获取第三层文件列表
    const getBooks = (subcategory) => {
      // 添加调试日志
      console.log('获取文件列表:', subcategory, '当前标准库数据:', standardData.value)

      // 检查数据是否存在
      if (!standardData.value || !standardData.value[subcategory]) {
        console.log('未找到子分类数据:', subcategory)
        return []
      }

      // 检查是否有files字段
      if (!standardData.value[subcategory].files) {
        console.log('子分类数据中缺少files字段:', subcategory)
        return []
      }

      // 检查哪些书籍已经导入
      const importedBooks = myBooks.value.map(book => book.name)

      console.log('文件列表数据:', standardData.value[subcategory].files)
      return standardData.value[subcategory].files.map(file => {
        const name = file.replace('.xlsx', '')
        return {
          name: name,
          desc: file,
          fileName: file,
          category: selectedCategoryLevel1.value,
          subcategory: subcategory,
          down: importedBooks.includes(name)
        }
      })
    }

    // 导入书籍
    const importBook = async (fileName) => {
      try {
        loading.value = true
        loadingMessage.value = `正在导入 ${getFileDisplayName(fileName)}...`

        // 获取当前路径信息
        let category = '';
        let subcategory = '';

        if (breadcrumbPaths.value.length > 0) {
          category = breadcrumbPaths.value[0]; // 第一级路径作为category
          subcategory = breadcrumbPaths.value[breadcrumbPaths.value.length - 1]; // 最后一级路径作为subcategory
        } else {
          // 如果在根目录，根据galleryType设置category
          category = galleryType.value === 'galleryE' ? 'E' : 'C';
        }

        const response = await AbhsService.importWords(
          user.value.id,
          getFileDisplayName(fileName),
          subcategory || getFileDisplayName(fileName),
          category,
          galleryType.value === 'galleryE' ? 'E' : 'C'
        )

        if (response.status === 200) {
          alert(`${getFileDisplayName(fileName)} 导入成功！`)
          // 更新我的单词表
          await getMyWordLists()
        } else {
          alert(`${getFileDisplayName(fileName)} 导入失败：${response.message}`)
        }
      } catch (error) {
        console.error('导入书籍失败:', error)
        alert(`${getFileDisplayName(fileName)} 导入失败`)
      } finally {
        loading.value = false
      }
    }

    // 选择单词表
    const selectWordList = async (book) => {
      selectedWordList.value = book
      // 找到当前选中单词表的索引
      currentWordListIndex.value = myBooks.value.findIndex(b => b.name === book.name)
      activeTab.value = 'practice'
      await loadWordsForSelectedList()
    }

    // 为选中的单词表加载单词
    const loadWordsForSelectedList = async () => {
      if (!selectedWordList.value) return

      try {
        loading.value = true
        loadingMessage.value = '加载单词...'
        const response = await AbhsService.getWords(
          user.value.id,
          selectedWordList.value.name
        )
        if (response.status === 200) {
          shuffledWords.value = shuffleArray(response.message || [])
          resetPractice()
        }
      } catch (error) {
        console.error('加载单词失败:', error)
      } finally {
        loading.value = false
      }
    }

    // 切换到上一个单词表
    const switchToPreviousWordList = async () => {
      if (currentWordListIndex.value > 0) {
        currentWordListIndex.value--
        selectedWordList.value = myBooks.value[currentWordListIndex.value]
        await loadWordsForSelectedList()
      }
    }

    // 切换到下一个单词表
    const switchToNextWordList = async () => {
      if (currentWordListIndex.value < myBooks.value.length - 1) {
        currentWordListIndex.value++
        selectedWordList.value = myBooks.value[currentWordListIndex.value]
        await loadWordsForSelectedList()
      }
    }

    // 开始练习
    const startPractice = async () => {
      if (shuffledWords.value.length === 0) {
        await loadWordsForSelectedList()
      }

      if (shuffledWords.value.length > 0) {
        isPracticing.value = true
        showResults.value = false
        resetPractice()
        await nextTick()
      } else {
        alert('当前单词表为空')
      }
    }

    // 停止练习
    const stopPractice = () => {
      isPracticing.value = false
      showResults.value = true
    }

    // 重置练习状态
    const resetPractice = () => {
      currentWordIndex.value = 0
      correctCount.value = 0
      wrongCount.value = 0
      wrongWords.value = []
      userAnswer.value = ''
      showResults.value = false
    }

    // 检查答案
    const checkAnswer = async () => {
      if (!currentWord.value || !userAnswer.value.trim()) return

      const isCorrect = userAnswer.value.trim().toLowerCase() === currentWord.value.name.toLowerCase()

      if (isCorrect) {
        correctCount.value++
      } else {
        wrongCount.value++
        wrongWords.value.push({
          name: currentWord.value.name,
          desc: currentWord.value.desc,
          userAnswer: userAnswer.value
        })
      }

      // 更新单词掌握程度
      try {
        await AbhsService.changeStrength(
          user.value.id,
          currentWord.value.fromNode_id,
          currentWord.value.toNode_id,
          isCorrect
        )
      } catch (error) {
        console.error('更新单词掌握程度失败:', error)
      }

      // 移动到下一个单词
      if (currentWordIndex.value < shuffledWords.value.length - 1) {
        currentWordIndex.value++
        userAnswer.value = ''
        await nextTick()
      } else {
        // 练习结束
        isPracticing.value = false
        showResults.value = true
      }
    }

    // 重新开始练习
    const restartPractice = () => {
      resetPractice()
      startPractice()
    }

    // 返回ABHS主页面
    const goToAbhsMain = () => {
      router.push('/abhs')
    }

    // 返回到"我的"选项卡
    const goToMyTab = () => {
      switchTab('my')
    }

    // 前往练习
    const goToPractice = (book) => {
      router.push({
        path: '/abhs/practice',
        query: {
          bookName: book.name,
          bookType: galleryType.value === 'galleryE' ? 'abhsWords' : 'abhsChinese'
        }
      })
    }

    // 编辑单词表
    const editBook = (book) => {
      // 跳转到编辑页面
      router.push({
        path: '/abhs/edit',
        query: {
          bookName: book.name
        }
      })
    }

    // 开始默写
    const startMemorization = (book) => {
      // 直接跳转到默写页面
      router.push({
        path: '/abhs/memorization',
        query: {
          bookName: book.name,
          bookType: galleryType.value === 'galleryE' ? 'abhsWords' : 'abhsChinese'
        }
      })
    }

    // 删除书籍
    const deleteBook = async (book) => {
      if (confirm(`确定要删除单词表 "${book.name}" 吗？`)) {
        try {
          loading.value = true
          loadingMessage.value = `正在删除 ${book.name}...`
          // 这里需要调用删除API，暂时用模拟
          await new Promise(resolve => setTimeout(resolve, 1000))
          alert(`${book.name} 删除成功！`)
          // 重新加载我的单词表
          await getMyWordLists()
        } catch (error) {
          console.error('删除书籍失败:', error)
          alert(`${book.name} 删除失败`)
        } finally {
          loading.value = false
        }
      }
    }

    // 显示排行榜
    const showRanking = async () => {
      try {
        loading.value = true
        loadingMessage.value = '加载排行榜...'
        const response = await AbhsService.getUserRanking()
        if (response.status === 200) {
          console.log('排行榜数据:', response.message)
          alert('排行榜功能正在开发中...')
        }
      } catch (error) {
        console.error('获取排行榜失败:', error)
        alert('获取排行榜失败')
      } finally {
        loading.value = false
      }
    }

    // 显示添加对话框
    const showAddDialog = () => {
      showAddDialogFlag.value = true
    }

    // 关闭添加对话框
    const closeAddDialog = () => {
      showAddDialogFlag.value = false
      newBookName.value = ''
      newBookDesc.value = ''
    }

    // 添加新单词表
    const addNewBook = async () => {
      if (!newBookName.value.trim()) {
        alert('请输入单词表名称')
        return
      }

      try {
        loading.value = true
        loadingMessage.value = `正在创建 ${newBookName.value}...`
        // 这里需要调用创建API，暂时用模拟
        await new Promise(resolve => setTimeout(resolve, 1000))
        alert(`${newBookName.value} 创建成功！`)
        closeAddDialog()
        // 重新加载我的单词表
        await getMyWordLists()
      } catch (error) {
        console.error('创建单词表失败:', error)
        alert(`${newBookName.value} 创建失败`)
      } finally {
        loading.value = false
      }
    }

    // 数组随机打乱
    const shuffleArray = (array) => {
      const newArray = [...array]
      for (let i = newArray.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [newArray[i], newArray[j]] = [newArray[j], newArray[i]]
      }
      return newArray
    }

    // 手动加载标准库数据
    const loadStandardLibrary = () => {
      console.log('手动加载标准库数据...')
      if (Object.keys(standardData.value).length === 0) {
        console.log('标准库数据为空，开始加载...')
        getStandardLibrary()
      } else {
        console.log('标准库数据已存在:', standardData.value)
        validateStandardData()
      }
    }

    // 重新加载标准库数据
    const reloadStandardLibrary = async () => {
      console.log('重新加载标准库数据...')
      // 清空现有数据
      standardData.value = {}
      categoryList.value = []
      // 重新加载
      await getStandardLibrary()
      console.log('重新加载完成，数据:', standardData.value)
    }

    // 测试API服务
    const testApiService = async () => {
      try {
        console.log('开始测试API服务...')
        const result = await AbhsService.testGetStandardLibrary()
        console.log('API服务测试结果:', result)

        // 详细分析响应结构
        if (result) {
          console.log('测试结果类型:', typeof result)
          console.log('测试结果结构:', result)
          if (result.data) {
            console.log('数据部分:', result.data)
            if (result.data.data) {
              console.log('实际数据:', result.data.data)
            }
          }
        }

        alert('API服务测试完成，请查看控制台日志')
      } catch (error) {
        console.error('API服务测试失败:', error)
        alert('API服务测试失败，请查看控制台日志')
      }
    }

    // 验证标准库数据完整性
    const validateStandardData = () => {
      console.log('开始验证标准库数据完整性...')
      console.log('当前标准库数据:', standardData.value)

      if (!standardData.value || Object.keys(standardData.value).length === 0) {
        console.log('标准库数据为空')
        return false
      }

      // 检查根节点
      if (!standardData.value.root) {
        console.log('缺少root节点')
        return false
      }

      // 检查根节点的文件和文件夹
      if (!standardData.value.root.files && !standardData.value.root.folders) {
        console.log('root节点缺少files或folders字段')
        return false
      }

      console.log('标准库数据验证通过')
      console.log('根节点数据:', standardData.value.root)
      return true
    }

    // 显示标准库数据结构
    const showStandardDataStructure = () => {
      console.log('标准库数据结构:', JSON.stringify(standardData.value, null, 2))

      // 详细显示数据结构
      if (standardData.value) {
        console.log('=== 标准库数据结构详情 ===')
        for (const key in standardData.value) {
          // 使用 Object.prototype.hasOwnProperty.call 替代 obj.hasOwnProperty
          if (Object.prototype.hasOwnProperty.call(standardData.value, key)) {
            const item = standardData.value[key]
            console.log(`键: ${key}`)
            if (item.files) {
              console.log(`  文件数量: ${item.files.length}`)
            }
            if (item.folders) {
              console.log(`  文件夹数量: ${item.folders.length}`)
              console.log(`  文件夹列表:`, item.folders)
            }
          }
        }
      }

      alert('请查看浏览器控制台以查看标准库数据结构')
    }

    // 格式化显示标准库数据
    const formatStandardData = () => {
      if (!validateStandardData()) {
        alert('标准库数据不完整或不存在')
        return
      }

      let output = '标准库数据结构:\n'
      output += '==================\n'

      // 显示根目录信息
      output += '根目录:\n'
      output += `  文件: ${standardData.value.root.files ? standardData.value.root.files.length : 0} 个\n`
      output += `  文件夹: ${standardData.value.root.folders ? standardData.value.root.folders.length : 0} 个\n`

      // 显示第一层分类
      if (standardData.value.root.folders && standardData.value.root.folders.length > 0) {
        output += '\n第一层分类:\n'
        standardData.value.root.folders.forEach(folder => {
          output += `  - ${folder}\n`
          if (standardData.value[folder]) {
            output += `    文件: ${standardData.value[folder].files ? standardData.value[folder].files.length : 0} 个\n`
            output += `    文件夹: ${standardData.value[folder].folders ? standardData.value[folder].folders.length : 0} 个\n`
          }
        })
      }

      console.log(output)
      alert(output)
    }

    // 初始化应用
    const initApp = async () => {
      try {
        console.log('Gallery系统初始化中...')

        // 加载用户信息和应用信息
        loadUserInfo()
        await loadAppInfo()

        // 确定Gallery类型
        if (route.path.includes('galleryC')) {
          galleryType.value = 'galleryC'
        } else {
          galleryType.value = 'galleryE'
        }

        // 获取我的单词表
        await getMyWordLists()

        console.log('Gallery系统初始化完成')
      } catch (error) {
        console.error('Gallery系统初始化失败:', error)
      } finally {
        loading.value = false
      }
    }

    // 组件挂载时初始化
    onMounted(() => {
      initApp()
      // 默认加载标准库数据
      loadStandardLibrary()
    })

    return {
      user,
      apps,
      loading,
      loadingMessage,
      galleryType,
      activeTab,
      activeCategory,
      myBooks,
      standardData,
      categoryList,
      showAddDialogFlag,
      newBookName,
      newBookDesc,
      selectedWordList,
      currentWordListIndex,
      isPracticing,
      shuffledWords,
      currentWordIndex,
      currentWord,
      userAnswer,
      correctCount,
      wrongCount,
      wrongWords,
      showResults,
      accuracy,
      selectedCategoryLevel1,
      selectedCategoryLevel2,
      breadcrumbPaths,
      currentLevelTitle,
      currentLevelData,
      currentFolders,
      currentFiles,
      switchTab,
      selectCategoryLevel1,
      selectCategoryLevel2,
      clearCategorySelection,
      clearCategoryLevel2Selection,
      enterFolder,
      getFileDisplayName,
      isBookImported,
      goBack,
      resetToRoot,
      navigateToPath,
      getRootCategories,
      getSubcategoriesLevel2,
      getBooks,
      importBook,
      selectWordList,
      switchToPreviousWordList,
      switchToNextWordList,
      startPractice,
      stopPractice,
      checkAnswer,
      restartPractice,
      goToAbhsMain,
      goToMyTab,
      goToPractice,
      editBook,
      startMemorization,
      deleteBook,
      showRanking,
      showAddDialog,
      closeAddDialog,
      addNewBook,
      // 添加手动加载方法
      loadStandardLibrary,
      // 添加重新加载方法
      reloadStandardLibrary,
      // 添加测试API服务方法
      testApiService,
      // 添加验证方法
      validateStandardData,
      // 添加显示数据结构方法
      showStandardDataStructure,
      // 添加格式化显示方法
      formatStandardData
    }
  }
}
</script>

<style scoped>
.gallery-container {
  min-height: 100vh;
  background-color: #f5f5f5;
}

.gallery-header {
  background-color: #fff;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.gallery-title {
  margin: 0;
  font-size: 24px;
  color: #333;
}

.gallery-main {
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
}

.tabs {
  display: flex;
  background-color: #fff;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin-bottom: 20px;
}

.tab {
  flex: 1;
  padding: 15px;
  border: none;
  background-color: #f8f9fa;
  cursor: pointer;
  font-size: 16px;
  transition: all 0.3s ease;
}

.tab:hover {
  background-color: #e9ecef;
}

.tab.active {
  background-color: #007bff;
  color: white;
}

.tab-content {
  background-color: #fff;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

/* 面包屑导航 */
.breadcrumb {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 1px solid #eee;
}

.breadcrumb-link {
  color: #007bff;
  cursor: pointer;
  margin-right: 5px;
}

.breadcrumb-link:hover {
  text-decoration: underline;
}

.breadcrumb-current {
  color: #666;
  margin-right: 10px;
}

.back-btn {
  margin-left: auto;
  background-color: #6c757d;
  color: white;
  border: none;
  padding: 5px 10px;
  border-radius: 4px;
  cursor: pointer;
}

.back-btn:hover {
  background-color: #5a6268;
}

/* 第一层分类 */
.category-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 20px;
}

.category-card {
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 20px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  background-color: #fff;
}

.category-card:hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}

.category-icon {
  font-size: 36px;
  margin-bottom: 10px;
}

.category-name {
  margin: 0;
  font-size: 18px;
  color: #333;
}

/* 第二层子分类 */
.subcategory-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 15px;
}

.subcategory-card {
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 15px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  background-color: #f8f9fa;
}

.subcategory-card:hover {
  background-color: #e9ecef;
  border-color: #007bff;
}

.subcategory-icon {
  font-size: 24px;
  margin-bottom: 8px;
}

.subcategory-name {
  margin: 0;
  font-size: 16px;
  color: #333;
}

/* 第三层文件列表 */
.books-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 20px;
}

.book-card {
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 15px;
  transition: all 0.3s ease;
  background-color: #fff;
  cursor: pointer;
}

.book-card:hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}

.book-card.my-book {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.book-info {
  margin-bottom: 15px;
}

.book-name {
  font-size: 16px;
  font-weight: bold;
  margin: 0 0 8px 0;
  color: #333;
}

.book-desc {
  font-size: 14px;
  color: #666;
  margin: 0 0 10px 0;
}

.book-stats {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: #888;
}

.stat-item {
  background-color: #f8f9fa;
  padding: 2px 6px;
  border-radius: 4px;
}

.book-actions {
  display: flex;
  gap: 10px;
}

.import-btn,
.practice-btn,
.edit-btn,
.delete-btn,
.confirm-btn,
.cancel-btn {
  padding: 6px 12px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s ease;
}

.import-btn {
  background-color: #28a745;
  color: white;
}

.import-btn:hover {
  background-color: #218838;
}

.practice-btn {
  background-color: #007bff;
  color: white;
}

.practice-btn:hover {
  background-color: #0056b3;
}

.edit-btn {
  background-color: #ffc107;
  color: #212529;
}

.edit-btn:hover {
  background-color: #e0a800;
}

.delete-btn {
  background-color: #dc3545;
  color: white;
}

.delete-btn:hover {
  background-color: #c82333;
}

.imported-tag {
  background-color: #6c757d;
  color: white;
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 14px;
}

.add-card {
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background-color: #f8f9fa;
  border: 2px dashed #ddd;
}

.add-content {
  text-align: center;
  color: #666;
}

.add-icon {
  font-size: 36px;
  line-height: 1;
}

.add-card:hover {
  background-color: #e9ecef;
  border-color: #007bff;
}

.empty-state {
  text-align: center;
  padding: 50px 20px;
}

.empty-state p {
  font-size: 18px;
  color: #666;
  margin: 0;
}

.dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.dialog {
  background-color: white;
  padding: 20px;
  border-radius: 8px;
  width: 90%;
  max-width: 500px;
}

.dialog h3 {
  margin-top: 0;
  color: #333;
}

.form-group {
  margin-bottom: 15px;
}

.form-group label {
  display: block;
  margin-bottom: 5px;
  font-weight: bold;
  color: #333;
}

.form-input,
.form-textarea {
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

.form-textarea {
  min-height: 80px;
  resize: vertical;
}

.dialog-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
}

.cancel-btn {
  background-color: #6c757d;
  color: white;
}

.cancel-btn:hover {
  background-color: #5a6268;
}

.confirm-btn {
  background-color: #007bff;
  color: white;
}

.confirm-btn:hover {
  background-color: #0056b3;
}

.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.loading-spinner {
  text-align: center;
  color: white;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #007bff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 10px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

.content-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid #eee;
}

.navigation {
  display: flex;
  align-items: center;
  gap: 15px;
}

.current-book {
  margin: 0;
  color: #333;
}

.nav-btn {
  background-color: #6c757d;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
}

.nav-btn:hover:not(:disabled) {
  background-color: #5a6268;
}

.nav-btn:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}

.controls {
  display: flex;
  gap: 10px;
}

.control-btn {
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
}

.start-btn {
  background-color: #28a745;
  color: white;
}

.start-btn:hover {
  background-color: #218838;
}

.stop-btn {
  background-color: #dc3545;
  color: white;
}

.stop-btn:hover {
  background-color: #c82333;
}

.ranking-btn {
  background-color: #17a2b8;
  color: white;
}

.ranking-btn:hover {
  background-color: #138496;
}

.practice-area {
  margin-top: 20px;
}

.word-display {
  text-align: center;
  margin-bottom: 30px;
}

.word {
  font-size: 36px;
  font-weight: bold;
  color: #333;
  margin-bottom: 10px;
}

.description {
  font-size: 18px;
  color: #666;
}

.user-input {
  display: flex;
  gap: 10px;
  margin-bottom: 30px;
  justify-content: center;
}

.answer-input {
  padding: 12px;
  font-size: 18px;
  border: 2px solid #ddd;
  border-radius: 4px;
  width: 300px;
}

.answer-input:focus {
  outline: none;
  border-color: #007bff;
}

.check-btn {
  background-color: #007bff;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 16px;
}

.check-btn:hover {
  background-color: #0056b3;
}

.practice-stats {
  display: flex;
  justify-content: center;
  gap: 30px;
  background-color: #f8f9fa;
  padding: 15px;
  border-radius: 8px;
}

.stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.stat-item .label {
  font-size: 14px;
  color: #666;
  margin-bottom: 5px;
}

.stat-item .value {
  font-size: 20px;
  font-weight: bold;
  color: #333;
}

.correct {
  color: #28a745;
}

.wrong {
  color: #dc3545;
}

.results {
  margin-top: 30px;
  padding: 20px;
  background-color: #f8f9fa;
  border-radius: 8px;
}

.results h3 {
  margin-top: 0;
  color: #333;
}

.result-stats {
  display: flex;
  justify-content: center;
  gap: 30px;
  margin-bottom: 30px;
}

.wrong-words {
  margin-top: 20px;
}

.wrong-words h4 {
  margin-top: 0;
  color: #333;
}

.wrong-words-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 15px;
}

.wrong-word-item {
  background-color: #fff;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 10px;
}

.wrong-word-item .word {
  font-weight: bold;
  color: #dc3545;
}

.wrong-word-item .desc {
  font-size: 14px;
  color: #666;
}

.result-actions {
  display: flex;
  justify-content: center;
  gap: 15px;
  margin-top: 30px;
}

.action-btn {
  background-color: #007bff;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 4px;
  cursor: pointer;
}

.action-btn:hover {
  background-color: #0056b3;
}

@media (max-width: 768px) {
  .gallery-main {
    padding: 10px;
  }

  .category-grid,
  .subcategory-grid,
  .books-grid {
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  }

  .tabs {
    flex-direction: column;
  }

  .content-header {
    flex-direction: column;
    gap: 15px;
  }

  .practice-stats {
    flex-wrap: wrap;
    gap: 15px;
  }

  .result-stats {
    flex-wrap: wrap;
    gap: 15px;
  }

  .user-input {
    flex-direction: column;
    align-items: center;
  }

  .answer-input {
    width: 100%;
    max-width: 300px;
  }

  .breadcrumb {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }

  .back-btn {
    margin-left: 0;
    align-self: flex-end;
  }
}

/* 添加测试按钮样式 */
.gallery-header button {
  margin: 5px;
  padding: 5px 10px;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.gallery-header button:hover {
  background-color: #0056b3;
}
</style>